import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

@Injectable()
export class KycMockInterceptor implements HttpInterceptor {
  private router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only mock requests when on the KYC route AND 'mock=true' is explicitly set in the browser address bar
    const hasMockParam =
      window.location.search.includes('mock=true') || window.location.hash.includes('mock=true');
    if (!this.router.url.includes('/kyc') || !hasMockParam) {
      return next.handle(request);
    }

    const isProfileUrl = request.url.includes('/accounts/profile');
    const isKycUrl = request.url.includes('/accounts/kyc/');

    if (!isProfileUrl && !isKycUrl) {
      return next.handle(request);
    }

    // Parse simulated status from browser URL search parameters or headers
    const browserUrl = new URL(window.location.href);
    const mockStatus =
      browserUrl.searchParams.get('mockStatus') || request.headers.get('X-Mock-Status');

    // Helper to construct HttpErrorResponse
    const makeError = (
      status: number,
      errorCode: string,
      message: string,
      extra: Record<string, any> = {}
    ) => {
      return new HttpErrorResponse({
        status,
        statusText: errorCode,
        url: request.url,
        error: {
          errorCode,
          message,
          ...extra,
        },
      });
    };

    // Simulate failures based on status request
    if (mockStatus) {
      if (mockStatus === '401') {
        return throwError(() => makeError(401, 'UNAUTHORIZED', 'Session expired')).pipe(delay(500));
      }
      if (mockStatus === '404') {
        return throwError(() =>
          makeError(404, 'KYC_VERSION_NOT_FOUND', 'KYC Version not found')
        ).pipe(delay(500));
      }
      if (mockStatus === '409-stale') {
        return throwError(() =>
          makeError(409, 'KYC_VERSION_STALE', 'Questionnaire version is stale')
        ).pipe(delay(500));
      }
      if (mockStatus === '409-submitted') {
        return throwError(() =>
          makeError(409, 'KYC_ALREADY_SUBMITTED', 'KYC already submitted')
        ).pipe(delay(500));
      }
      if (mockStatus === '409-not-required') {
        return throwError(() => makeError(409, 'KYC_NOT_REQUIRED', 'KYC not required')).pipe(
          delay(500)
        );
      }
      if (mockStatus === '422') {
        return throwError(() =>
          makeError(422, 'KYC_VALIDATION_FAILED', 'Validation failed', {
            fieldErrors: [
              {
                fieldName: 'expectedMonthlySpend',
                message: 'Spend is below minimum allowed value',
              },
              {
                fieldName: 'preferredCategories',
                message: 'You must select at least one preference',
              },
            ],
          })
        ).pipe(delay(500));
      }
      if (mockStatus === '503') {
        return throwError(() =>
          makeError(503, 'SERVICE_UNAVAILABLE', 'Service temporarily unavailable')
        ).pipe(delay(500));
      }
      if (mockStatus === 'offline') {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 0,
              statusText: 'Unknown Error',
              url: request.url,
              error: new ProgressEvent('error'),
            })
        ).pipe(delay(500));
      }
    }

    // Real Mock Routing Logic
    // 1. GET /accounts/profile
    if (isProfileUrl && request.method === 'GET') {
      const isDone = browserUrl.searchParams.get('profileState') === 'done';
      const isNull = browserUrl.searchParams.get('profileState') === 'null';

      const profileData = {
        kyc: isDone ? 'DONE' : 'REQUIRED',
        kycVersionRequired: isNull ? null : 4,
        kycVersionAccepted: isDone ? 4 : 3,
      };

      return of(new HttpResponse({ status: 200, body: profileData })).pipe(delay(600));
    }

    // 2. GET /accounts/kyc/versions/{version}
    if (request.url.includes('/accounts/kyc/versions/') && request.method === 'GET') {
      const versionMatch = request.url.match(/\/versions\/(\d+)/);
      const version = versionMatch ? parseInt(versionMatch[1], 10) : 4;

      const questionnaireData = {
        version: version,
        status: 'ACTIVE',
        fields: [
          {
            fieldName: 'monthlyOrders',
            type: 'ENUM',
            required: true,
            displayOrder: 1,
            label: {
              en: 'How many orders are you going to make every month ?',
              ar: 'كم عدد الطلبات التي ستقوم بها كل شهر ؟',
            },
            options: [
              {
                code: 'ORDERS_LESS_THAN_10',
                displayOrder: 1,
                label: { en: 'Less than 10 orders', ar: 'أقل من 10 طلبات' },
              },
              {
                code: 'ORDERS_10_TO_20',
                displayOrder: 2,
                label: { en: '10 - 20 orders', ar: '10 - 20 طلب' },
              },
              {
                code: 'ORDERS_MORE_THAN_20',
                displayOrder: 3,
                label: { en: 'More than 20 orders', ar: 'أكثر من 20 طلب' },
              },
            ],
          },
          {
            fieldName: 'expectedMonthlySpend',
            type: 'NUMBER',
            required: true,
            displayOrder: 2,
            numberMin: 0,
            numberMax: 99999,
            label: {
              en: 'How much in KD do you think you are going to pay using Taly?',
              ar: 'ما هو المبلغ بالدينار الكويتي الذي تعتقد أنك ستدفعه باستخدام تالي؟',
            },
          },
        ],
      };

      return of(new HttpResponse({ status: 200, body: questionnaireData })).pipe(delay(600));
    }

    // 3. POST /accounts/kyc/submissions
    if (request.url.includes('/accounts/kyc/submissions') && request.method === 'POST') {
      const isIdempotent = browserUrl.searchParams.get('idempotent') === 'true';
      const body = request.body as any;

      const submissionResult = {
        id: Math.floor(Math.random() * 1000) + 1,
        version: body?.version || 4,
        idempotent: isIdempotent,
      };

      return of(new HttpResponse({ status: 200, body: submissionResult })).pipe(delay(800));
    }

    return next.handle(request);
  }
}
